import { makeTree } from '../../classifier/tree';
import { ClassificationDoesNotExists, FeatureDoesNotExists } from '../exceptions';


/**
 * Reset the items, delete the array and set the total as zero.
 * @param {Object} state
 */
function resetItems(state) {
  setItems(state, [])
  setItemsTotal(state, 0)
}


/**
 * Set the active tab where the app is showing the content.
 * @param {Object} state
 * @param {Number} tab
 */
function setActiveTab(state, tab) {
  state.activeTab = tab
}


/**
 * Set the brands of the selected classification.
 * @param {Object} state
 * @param {String[]} brands 
 */
function setBrands(state, brands) {
  state.brands = brands || []
  setSelectedBrands(state, [])
}


/**
 * AVOID USING THIS MUTATION DIRECTLY, INSTEAD USE THE ACTION `setClassification`.
 * The action will trigger other actions to get the data that depends on the selected
 * classification.
 * 
 * Set the selected classification in the store.
 * 
 * @param {Object} state
 * @param {Number} id the id of the classification to set.
 */
function setClassification(state, id) {
  const classification = state.classifications.find(c => c.id === id)
  if (id && !classification) throw new ClassificationDoesNotExists()

  state.classification = classification ? { ...classification } : null
  if (!classification) {
    state.features = []
    setFeature(state, null)
    setBrands(state, [])
  }
}


/**
 * Set the array of classification objects in the store.
 * @param {Object} state
 * @param {Object[]} classifications the array with the classifications to set.
 */
function setClassifications(state, classifications) {
  state.classifications = makeTree(classifications);
}


/**
 * AVOID USING THIS MUTATION DIRECTLY, USE THE ACTION `setFeature` INSTEAD.
 * The action will trigger other actions useful for the app.
 * Set the selected feature in the store.
 * @param {Object} state
 * @param {Number} id the id of the feature to set.
 */
function setFeature(state, id) {
  const feature = state.features.find(f => f.id === id)
  if (id && !feature) throw new FeatureDoesNotExists()

  state.feature = feature ? { ...feature } : null
  if (!feature) {
    state.featureValue = null
    state.featureValues = []
    state.items = []
    state.itemsTotal = null
    state.likePattern.like = null
    state.likePattern.notLike = null
    state.rules = []
    state.rulesTotal = null
    state.selectedBrands = null
  }
}


/**
 * Set the features in the store to select one of them.
 * @param {Object} state
 * @param {Object[]} features
 */
function setFeatures(state, features) {
  state.features = features
}


/**
 * Set the value to save as feature for the matching items.
 * @param {Object} state
 * @param {String} value
 */
function setFeatureValue(state, value) {
  state.featureValue = value
}


/**
 * Set all the distinct values that were used in the past with the selected feature.
 * @param {Object} state
 * @param {String[]} values
 */
function setFeatureValues(state, values) {
  state.featureValues = values
}


/**
 * Set the items that matched with the configurations.
 * @param {Object} state
 * @param {Object[]} items
 */
function setItems(state, items) {
  state.items = items
}


/**
 * AVOID USING THIS MUTATION DIRECTLY, PLEASE USE THE ACTION INSTEAD.
 * The action will change load the items of the different pages and will sort the elements
 * according to the pagination object.
 *
 * Set the pagination objects that controls the table of items.
 * @param {Object} state
 * @param {Object} pagination
 */
function setItemsPagination(state, pagination) {
  state.itemsPagination = pagination
}


/**
 * Set the total number of items that matches the rule.
 * @param {Object} state
 * @param {Number} total
 */
function setItemsTotal(state, total) {
  state.itemsTotal = total
}


/**
 * Set the pattern to search items.
 * @param {Object} state
 * @param {String} pattern
 */
function setLikePattern(state, pattern) {
  state.likePattern = pattern
  /* If the pattern changes the items are not the same, they must be populated with the getItems action */
  resetItems(state)
}


/**
 * Set the flag that indicates that the app is loading the selected classification's brands.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingBrands(state, value) {
  state.loadingBrands = value
}


/**
 * Set the loading flag that indicates that the app is loading the classifications.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingClassifications(state, value) {
  state.loadingClassifications = value
}


/**
 * Set the flag that indicates that the app is creating a new feature for the selected
 * classification.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingCreateFeature(state, value) {
  state.loadingCreateFeature = value
}


/**
 * Set the loading flag that indicates that the app is loading the features of the
 * selected classification.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingFeatures(state, value) {
  state.loadingFeatures = value
}


/**
 * Set the flag that indicates that the app is loading the distinct values of the selected feature.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingFeatureValues(state, value) {
  state.loadingFeatureValues = value
}


/**
 * Set the flag that indicates that the app is getting the items from the backend.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingItems(state, value) {
  state.loadingItems = value
}


/**
 * Set the flag that indicates that the app is removing a feature.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingRemoveFeature(state, value) {
  state.loadingRemoveFeature = value
}


/**
 * Set the flag that indicates that the app is loading the rules for
 * the selected classification and feature from the backend.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingRules(state, value) {
  state.loadingRules = value
}


/**
 * Set the flag to indicate that the app is saving the rule.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingSaveRule(state, value) {
  state.loadingSaveRule = value
}


/**
 * Set the onlyMissing flag to indicate to load only the items that does not have the feature.
 * @param {Object} state
 * @param {Boolean} value
 */
function setOnlyMissing(state, value) {
  state.onlyMissing = value
  resetItems(state)
}


/**
 * Set the rules already created for the selected classification and feature.
 * @param {Object} state
 * @param {Object[]} rules 
 */
function setRules(state, rules) {
  state.rules = rules
}


/**
 * Set the pagination object of the rules.
 * @param {Object} state
 * @param {Object} pagination
 */
function setRulesPagination(state, pagination) {
  state.rulesPagination = pagination
}


/**
 * Set the number of rules that are for the selected classification and feature
 * @param {Object} state
 * @param {Number} value
 */
function setRulesTotal(state, value) {
  state.rulesTotal = value
}


/**
 * Set the selected brands by the user to filter the items and get only items of these brands.
 * @param {Object} state 
 * @param {String[]} brands 
 */
function setSelectedBrands(state, brands) {
  state.selectedBrands = brands
  resetItems(state)
}


/**
 * Set the flag to show or hide the sidebar with the configurations.
 * @param {Object} state
 * @param {Boolean} value
 */
function setShowSidebar(state, value) {
  state.showSidebar = value
}


export default {
  resetItems,
  setActiveTab,
  setBrands,
  setClassification,
  setClassifications,
  setFeature,
  setFeatures,
  setFeatureValue,
  setFeatureValues,
  setItems,
  setItemsPagination,
  setItemsTotal,
  setLikePattern,
  setLoadingBrands,
  setLoadingClassifications,
  setLoadingCreateFeature,
  setLoadingFeatures,
  setLoadingFeatureValues,
  setLoadingItems,
  setLoadingRules,
  setLoadingRemoveFeature,
  setLoadingSaveRule,
  setOnlyMissing,
  setRules,
  setRulesPagination,
  setRulesTotal,
  setSelectedBrands,
  setShowSidebar
}